/**
 * A ponte entre o bloco Lit e o gráfico React — o elo que faltava.
 *
 * ## Por que este arquivo existe
 *
 * `bloco.ts` declarava dois campos que **ninguém preenchia**:
 *
 * ```ts
 * reactParaLit?: ReactParaLit;
 * renderizarGrafico?: (props: {...}) => ElementOrFactory;
 * ```
 *
 * e caía num aviso educado — *"Este gráfico não pode ser desenhado aqui"* — quando qualquer um
 * dos dois faltasse. Faltavam **os dois**, sempre, em toda instalação. O aviso existia para o
 * caso de um sabor da aplicação que não registra a extensão (a `preview-page`); acabou sendo o
 * único caminho que o código percorria.
 *
 * É a mesma família das regras 8.1/8.4/8.18.1/8.26/8.32/8.46.1/8.52: campo declarado, consumido
 * — e sem escritor. Aqui foram **dois num arquivo só**, e o `Grafico` de `grafico.tsx` ficou
 * exportado sem nenhum importador.
 *
 * ## Por que `.tsx` e não dentro do `index.ts`
 *
 * O `index.ts` é o registro, e registro não desenha. Aqui há JSX de verdade, e o arquivo existe
 * para que a conversão `props do modelo -> props do componente` tenha **um lugar só** (Regra
 * #9) — em vez de viver espalhada entre o `setup()` da extensão e o `renderBlock` do bloco.
 */

import type { ElementOrFactory } from '@affine/component';

import type { LinhaDeDados } from './dados';
import { Grafico } from './grafico';
import type { TuninhoChartBlockModel } from './model';

/**
 * Lê o modelo e devolve o gráfico React pronto para a ponte.
 *
 * ⚠️ Devolve uma **fábrica**, não um elemento. O `reactToLit` do Affine aceita os dois
 * (`ElementOrFactory`), e a fábrica é a forma certa aqui: ela adia a criação para o momento em
 * que a ponte monta, que é o que permite o Lit descartar e remontar sem construir React à toa
 * a cada `renderBlock`.
 */
export function renderizarGraficoDoModelo({
  linhas,
  modelo,
}: {
  linhas: LinhaDeDados[];
  modelo: TuninhoChartBlockModel;
}): ElementOrFactory {
  return () => (
    <Grafico
      linhas={linhas}
      tipo={modelo.props.tipo$.value}
      eixoX={modelo.props.eixoX$.value}
      series={modelo.props.series$.value}
      agregacao={modelo.props.agregacao$.value}
      altura={modelo.props.altura$.value}
    />
  );
}
